import { Controller } from "@hotwired/stimulus"

// PR #04d — X-19 solver tier-weights live ratio check.
//
// Watches the four tier inputs and surfaces a warning banner whenever the
// ratio between adjacent tiers (coverage > hard > soft > preference) drops
// below `minRatioValue` (default 10x). The server-side helper rule of thumb
// is "keep at least 10x between tiers"; this controller is purely visual.
//
// Targets:
//   input         — one <input type="number"> per tier (data-tier-key="…")
//   warning       — banner element with the "hidden" class toggled
//   warningDetail — span used to show which pair tripped the warning
//
// Values:
//   minRatio      — minimum acceptable ratio between adjacent tiers
export default class extends Controller {
  static targets = ["input", "warning", "warningDetail"]
  static values = { minRatio: { type: Number, default: 10 } }

  // Order matters: coverage > hard > soft > preference. The check verifies
  // that each tier is at least minRatio× the next one.
  TIER_ORDER = ["coverage", "hard", "soft", "preference"]

  connect() {
    this.check()
  }

  check() {
    const values = {}
    for (const input of this.inputTargets) {
      const key = input.dataset.tierKey
      const val = parseFloat(input.value)
      if (Number.isFinite(val) && val > 0) values[key] = val
    }

    const issues = []
    for (let i = 0; i < this.TIER_ORDER.length - 1; i++) {
      const high = this.TIER_ORDER[i]
      const low = this.TIER_ORDER[i + 1]
      const hv = values[high]
      const lv = values[low]
      if (hv == null || lv == null) continue
      const ratio = hv / lv
      if (ratio < this.minRatioValue) {
        issues.push(`${high}/${low}=${ratio.toFixed(1)}x`)
      }
    }

    if (issues.length === 0) {
      this.warningTarget.classList.add("hidden")
      this.warningDetailTarget.textContent = ""
    } else {
      this.warningTarget.classList.remove("hidden")
      this.warningDetailTarget.textContent = ` (${issues.join(", ")})`
    }
  }
}
